import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useState } from "react";

import { BookingForm } from "@/components/booking-form";
import { ChecklistDownloadGate } from "@/components/checklist-download-gate";
import { BusinessChecklistGate } from "@/components/business-checklist-gate";
import { DutyCalculator } from "@/components/duty-calculator";
import { EnquiryForm } from "@/components/enquiry-form";
import { EnquiryCta } from "@/components/enquiry-cta";

const GUIDE_CTA: Record<string, string> = {
  Conveyancing: "Ask a conveyancing solicitor",
  "Wills & Estates": "Ask about your will or estate",
  "Elder Law": "Ask about elder law",
  "Business & Commercial": "Ask about your business matter",
};
import { GrannyFlatEnquiryForm } from "@/components/granny-flat-enquiry-form";


import { GuideRelatedAndNextSteps } from "@/components/guide-related";
import { GuideSidebar, headingId } from "@/components/guide-sidebar";
import { ProcessTimeline } from "@/components/process-timeline";
import { CATEGORY_HERO, GUIDE_DIAGRAMS, SITE_ORIGIN, categoryOgImage } from "@/lib/guide-visuals";
import { FaqList } from "@/components/page-parts";

import { FEES, FIRM, PEOPLE, faqSchema } from "@/lib/firm";
import { getGuide, type Guide, isDraftGuide, MORETON_BAY_GUIDE_SLUGS } from "@/lib/guides";

/** Old guide URLs that have been replaced — keep shared links working. */
const GUIDE_REDIRECTS: Record<string, string> = {
  "subject-to-sale-contracts-and-bridging-finance-queensland": "possession-before-settlement-queensland",
};

export const Route = createFileRoute("/guides/$slug")({
  loader: ({ params }) => {
    const moved = GUIDE_REDIRECTS[params.slug];
    if (moved) {
      throw redirect({ to: "/guides/$slug", params: { slug: moved }, statusCode: 301 });
    }
    const guide = getGuide(params.slug);
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [{ title: fitTitle("Guide unavailable") }, { name: "robots", content: "noindex" }],
      };
    }
    const g = loaderData.guide;
    const url = `${SITE_ORIGIN}/guides/${params.slug}`;
    return {
      meta: [
        { title: fitTitle(g.metaTitle) },
        { name: "description", content: fitDescription(g.description) },
        { property: "og:title", content: fitTitle(g.title) },
        { property: "og:description", content: fitDescription(g.description) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: categoryOgImage(g.category) },
        { property: "og:locale", content: "en_AU" },
        { name: "geo.region", content: "AU-QLD" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: categoryOgImage(g.category) },
        ...(isDraftGuide(g.slug) ? [{ name: "robots", content: "noindex" }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: g.title,
            description: g.description,
            inLanguage: "en-AU",
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            author: {
              "@type": "Person",
              name: PEOPLE[0].name,
              jobTitle: PEOPLE[0].role,
              worksFor: { "@id": "https://coastsidelaw.com.au/#legalservice" },
            },
            publisher: {
              "@type": "Organization",
              name: FIRM.name,
              url: SITE_ORIGIN,
              logo: `${SITE_ORIGIN}/icon-512.png`,
            },
            about: g.category,
            spatialCoverage: { "@type": "State", name: "Queensland, Australia" },
            image: categoryOgImage(g.category),
            ...(g.lastmod ? { dateModified: g.lastmod } : { datePublished: `${g.updated}-01-01`, dateModified: `${g.updated}-01-01` }),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
              { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE_ORIGIN}/guides` },
              { "@type": "ListItem", position: 3, name: g.title, item: url },
            ],
          }),
        },
        ...(g.faqs.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(faqSchema(g.faqs)),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: GuideNotFound,
  component: GuidePage,
});

function GuideNotFound() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-24 text-center">
      <h1 className="font-serif text-3xl text-foreground">Guide not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        That guide doesn't exist or has moved.
      </p>
      <Link to="/guides" className="mt-6 inline-block text-sm font-semibold text-primary underline">
        Browse all guides
      </Link>
    </div>
  );
}

function GuidePage() {
  const { guide } = Route.useLoaderData() as { guide: Guide };

  return (
    <>
      <article>
        <header className="border-b border-border bg-secondary/40">
          <div className="mx-auto max-w-3xl px-5 py-16">
            <nav className="text-xs text-muted-foreground">
              <Link to="/guides" className="underline">
                Guides
              </Link>
              <span className="mx-2">/</span>
              <span>{guide.category}</span>
            </nav>
            <h1 className="mt-4 font-serif text-4xl leading-tight text-foreground sm:text-5xl">
              {guide.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{guide.intro}</p>
            <EnquiryCta href="#contact-panel" label={GUIDE_CTA[guide.category] ?? "Ask us about your situation"} />
            {guide.slug === "how-much-does-conveyancing-cost-in-queensland" && (
              <p className="mt-4 border-l-2 border-primary pl-4 text-base leading-relaxed text-muted-foreground">
                Comparing quotes?{" "}
                <Link to="/fees" className="font-semibold text-primary underline">
                  See our fixed fees and itemised third-party costs
                </Link>{" "}
                or{" "}
                <Link to="/contact" className="font-semibold text-primary underline">
                  request a written quote for your property
                </Link>
                .
              </p>
            )}
            {guide.slug === "diy-will-kit-vs-solicitor-drafted-will-queensland" && (
              <p className="mt-4 rounded-sm border border-primary/40 bg-primary/5 p-4 text-sm leading-relaxed text-muted-foreground">
                Want a solicitor to draft it instead?{" "}
                <Link to="/fixed-price-will-solicitor" className="font-semibold text-primary underline">
                  See our fixed-price solicitor will (from $660 incl. GST)
                </Link>
                .
              </p>
            )}
            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
              Written by{" "}
              <Link to="/michael-klein" className="font-semibold text-foreground underline">
                {PEOPLE[0].name}
              </Link>
              , {PEOPLE[0].role}, admitted {PEOPLE[0].admitted} · General information about
              Queensland law · Last reviewed {guide.updated}
            </p>


            <div className="mt-8 flex flex-col gap-3 rounded-sm border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-relaxed text-foreground">
                <span className="font-semibold">Need advice on your own matter?</span>{" "}
                <span className="text-muted-foreground">
                  Book a no-obligation consultation with {FIRM.name} in {FIRM.suburb}.
                </span>
              </p>
              <div className="flex shrink-0 flex-wrap gap-2">
                <a
                  href="#consultation"
                  className="inline-flex items-center justify-center rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Request a consultation
                </a>
                <a
                  href={FIRM.phoneHref}
                  className="inline-flex items-center justify-center rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                >
                  Call {FIRM.phone}
                </a>
              </div>
            </div>
          </div>
        </header>

        <div className="border-b border-border bg-secondary/40">
          <div className="mx-auto max-w-3xl px-5 pb-14">
            <img
              src={CATEGORY_HERO[guide.category].src}
              alt={CATEGORY_HERO[guide.category].alt}
              width={1600}
              height={912}
              className="aspect-[16/7] w-full rounded-sm border border-border object-cover"
            />
          </div>
        </div>


        <div className="mx-auto grid max-w-3xl gap-14 px-5 py-14 xl:max-w-6xl xl:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="min-w-0">
          {guide.download &&
            (guide.download.href === "/business-sale-purchase-checklist.pdf" ? (
              <BusinessChecklistGate
                href={guide.download.href}
                label={guide.download.label}
                note={guide.download.note}
                guideTitle={guide.title}
              />
            ) : guide.download.gated ? (
              <ChecklistDownloadGate
                href={guide.download.href}
                label={guide.download.label}
                note={guide.download.note}
                guideTitle={guide.title}
              />
            ) : (
              <aside className="mb-12 rounded-sm border border-border bg-secondary/50 p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                  Free download
                </p>
                <h2 className="mt-2 font-serif text-xl text-foreground">{guide.download.label}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {guide.download.note}
                </p>
                <a
                  href={guide.download.href}
                  download
                  className="mt-5 inline-flex items-center gap-2 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Download the checklist (PDF)
                </a>
              </aside>
            ))}


          {guide.slug === "stamp-duty-queensland" && <DutyCalculator />}

          {GUIDE_DIAGRAMS[guide.slug] && (
            <ProcessTimeline
              title={GUIDE_DIAGRAMS[guide.slug]!.title}
              steps={GUIDE_DIAGRAMS[guide.slug]!.steps}
              note={GUIDE_DIAGRAMS[guide.slug]!.note}
            />
          )}

          {guide.sections.map((section) => (
            <section key={section.heading} id={headingId(section.heading)} className="mb-12 scroll-mt-28">
              <h2 className="font-serif text-2xl text-foreground">{section.heading}</h2>
              {section.paragraphs.map((p) => (
                <p key={p} className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
              {section.bullets && (
                <ul className="mt-5 space-y-2.5">
                  {section.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
          {guide.faqs.length > 0 && (
            <section id="faq" className="mb-4 scroll-mt-28">
              <h2 className="font-serif text-2xl text-foreground">
                {guide.faqHeading ?? "Frequently asked questions"}
              </h2>
              <div className="mt-6">
                <FaqList items={guide.faqs} />
              </div>
            </section>
          )}

          {guide.sources && (
            <section className="mt-12" aria-labelledby="guide-sources">
              <h2 id="guide-sources" className="font-serif text-2xl text-foreground">Queensland sources</h2>
              <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                {guide.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{source.label}</a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {MORETON_BAY_GUIDE_SLUGS.has(guide.slug) && (
            <aside className="mt-10 rounded-sm border border-primary/40 bg-primary/5 p-5 text-base leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground">On the Redcliffe Peninsula?</span>{" "}
              We see clients at our Scarborough office by appointment.{" "}
              <Link to="/scarborough" className="font-semibold text-primary underline">
                Solicitors Redcliffe &amp; Scarborough
              </Link>{" "}
              ·{" "}
              <a href="#contact-panel" className="font-semibold text-primary underline">
                Send an enquiry
              </a>
            </aside>
          )}

          <p className="mt-10 rounded-sm border-l-2 border-accent bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            This guide is general information about Queensland law, current at the time of writing. It
            is not legal advice and does not take your circumstances into account. Call {FIRM.name} on{" "}
            <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
              {FIRM.phone}
            </a>{" "}
            for advice on your own matter.
          </p>

          <GuideRelatedAndNextSteps guide={guide} />
          </div>

          <GuideSidebar
            headings={guide.sections.map((s) => s.heading)}
            hasFaq={guide.faqs.length > 0}
          />
        </div>

      </article>

      <GuideConsultationCta guide={guide} />
      <div className="h-20" />
    </>
  );
}

const CTA_COPY: Record<Guide["category"], { heading: string; body: string }> = {
  Conveyancing: {
    heading: "Get a fixed-fee conveyancing quote",
    body: `Send us the contract or just the details and we'll confirm the fixed fee — ${FEES.sale} to sell, ${FEES.purchase} to buy, both including GST — and the next steps on your timeline.`,
  },
  "Wills & Estates": {
    heading: "Book a wills and estates consultation",
    body: "Tell us briefly what you're dealing with — a will, an estate, probate or a claim — and we'll come back with what's involved, what it costs and how long it takes.",
  },
  "Elder Law": {
    heading: "Book an elder law consultation",
    body: "Powers of attorney, granny flat arrangements, retirement village contracts and aged care decisions. We'll talk it through in plain English, with family welcome in the room.",
  },
  "Business & Commercial": {
    heading: "Book a commercial consultation",
    body: "Buying or selling a business, or dealing with management rights? Send us the contract or the outline and we'll set out the steps, the risks and a fee estimate.",
  },
};

const MATTER_BY_CATEGORY: Record<
  Guide["category"],
  "conveyancing-purchase" | "wills-estates" | "elder-law" | "business-commercial"
> = {
  Conveyancing: "conveyancing-purchase",
  "Wills & Estates": "wills-estates",
  "Elder Law": "elder-law",
  "Business & Commercial": "business-commercial",
};

function GuideConsultationCta({ guide }: { guide: Guide }) {
  const copy = CTA_COPY[guide.category];
  const isGrannyFlat = guide.slug.startsWith("granny-flat");
  const [mode, setMode] = useState<"book" | "enquire">("book");

  return (
    <section id="consultation" className="mt-20 scroll-mt-24 border-y border-border bg-secondary/40">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 lg:grid-cols-2 lg:gap-16">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-accent">
            Speak to a lawyer
          </p>
          <h2 className="mt-3 font-serif text-3xl leading-tight text-foreground">{copy.heading}</h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{copy.body}</p>
          <ul className="mt-6 space-y-2.5">
            {[
              "A no-obligation first conversation about your matter",
              "Fixed fees quoted in writing before we start",
              `Local office at ${FIRM.street}, ${FIRM.suburb}`,
              "Members of the Queensland Law Society",
            ].map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={FIRM.phoneHref}
              className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Call {FIRM.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              Office details
            </Link>
          </div>
        </div>

        <div id="contact-panel" className="scroll-mt-24 rounded-sm border border-border bg-card p-6 sm:p-8">
          <div className="mb-6 grid grid-cols-2 gap-1 rounded-sm bg-secondary p-1">
            {([
              { value: "book", label: "Pick a time" },
              { value: "enquire", label: "Send an enquiry" },
            ] as const).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setMode(tab.value)}
                aria-pressed={mode === tab.value}
                className={
                  mode === tab.value
                    ? "rounded-sm bg-card px-3 py-2 text-sm font-semibold text-foreground shadow-sm"
                    : "rounded-sm px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                }
              >
                {tab.label}
              </button>
            ))}
          </div>

          {mode === "book" ? (
            <BookingForm
              heading="Choose an appointment time"
              defaultMatterType={MATTER_BY_CATEGORY[guide.category]}
            />
          ) : isGrannyFlat ? (
            <GrannyFlatEnquiryForm heading="Granny flat consultation" />
          ) : (
            <EnquiryForm heading="Send an enquiry" />
          )}
        </div>

      </div>
    </section>
  );
}
